/**
 * 🔴 **WHICH BUILD AM I LOOKING AT** — the stamp on the bottom rail (`2026 - W37`).
 *
 * Metaship's rail carries it and it earns its place for one reason: it is the first question of every
 * support conversation. "It's doing X" is unanswerable without "on what?", and a person who can read
 * the answer off the screen they are already looking at does not have to be walked through a menu to
 * find it.
 *
 * 🔴 **The ISO WEEK of the BUILD, not of today.** A rail that computed the current week would say
 * `W37` all week regardless of what is actually running — a number that changes on its own and never
 * because anything shipped. The stamp is injected at build time (`vite.config.ts`, `__RELEASE__`) and
 * is therefore a fact about the bundle.
 *
 * The ISO week and not a date because that is how the fleet talks about releases: a week is the unit
 * things ship in, and "W37" locates a build in a conversation the way "2026-09-07" does not.
 */

declare const __RELEASE__: string | undefined;

/** `2026-09-07T…` → `2026 - W37`. Exported for the test; the rail reads `RELEASE_LABEL`. */
export function isoWeekLabel(when: Date): string {
  // ISO-8601: weeks start Monday, and week 1 is the one containing the first Thursday. Shifting to
  // that Thursday first is what makes both rules true at once — including the turn of the year, where
  // 1 January can legitimately belong to week 52 or 53 of the year before.
  const thursday = new Date(Date.UTC(when.getUTCFullYear(), when.getUTCMonth(), when.getUTCDate()));
  const dayIndex = (thursday.getUTCDay() + 6) % 7; // Monday = 0
  thursday.setUTCDate(thursday.getUTCDate() - dayIndex + 3);
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const firstDayIndex = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayIndex + 3);
  const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return `${thursday.getUTCFullYear()} - W${String(week).padStart(2, "0")}`;
}

/**
 * When this bundle was built, as an ISO instant — or null in a dev server, where there is no build and
 * saying "2026 - W37" about a page that recompiled two seconds ago would be a small lie told
 * constantly.
 */
export const RELEASE_AT: string | null = typeof __RELEASE__ === "string" && __RELEASE__ !== "" ? __RELEASE__ : null;

/** What the rail draws, or null in dev — the rail then omits the stamp rather than inventing one. */
export const RELEASE_LABEL: string | null = RELEASE_AT === null ? null : isoWeekLabel(new Date(RELEASE_AT));
